import { ApplicationModuleTypes, NativeElementTags } from "../../globals";
import { KeyCodes } from "../../interfaces/Input";
import store from "../../state/store";
import WorkspaceManager from "../WorkspaceManager";

export default class WorkspaceUtils
{
	static isModalOpen()
	{
		return Object.keys(store.getState().workspaceModule.modals).length > 0;
	}

	static isModuleInputAccepting(window:string, acceptingModules: string[]): boolean 
	{
		for (const module of acceptingModules)
		{
			if (window.startsWith(module))
			{
				return true;
			}
		}
		return false;
	}
	/**
	 * used when determining if input shortcuts should call application events, or allow the browser's native behavior (such as pasting in input fields)
	 * @static
	 * @returns true if a native browser element (such as an input field) is in focus, or if any module that should normally block input is in focus
	 * @memberof Utils
	 */
	
	static shouldAllowNativeBehavior(ignoreWindows = false)
	{
		if (WorkspaceUtils.isModalOpen() || !document.activeElement) return true; // if a modal is open, or no element is focused, allow native behavior
		const activeWindow = store.getState().workspaceModule.activeWindow;
		const excludedModules = [
			ApplicationModuleTypes.SceneView,
			ApplicationModuleTypes.StepHierarchy,
			ApplicationModuleTypes.ObjectProperties,
			ApplicationModuleTypes.ARTrackers,
			ApplicationModuleTypes.TimelineEditor,
			ApplicationModuleTypes.ChatGPTEditor,
			ApplicationModuleTypes.VisageEditorWindow,
		];

		if (activeWindow === ApplicationModuleTypes.ChatGPTEditor)
		{
			return true;
		}

		const isModuleFocused = activeWindow && !this.isModuleInputAccepting(activeWindow, excludedModules);
		const isNativeElementFocused = NativeElementTags.includes(document.activeElement.tagName);

		if (ignoreWindows)
		{
			return isNativeElementFocused;
		}
		else
		{
			return isNativeElementFocused || isModuleFocused;
		}
	}
}

export function disableBrowserNativeInput()
{		  
	// prevent modified scrolling (page zoom etc)
	window.addEventListener("wheel", function (e)
	{
		if (e.altKey || e.ctrlKey || e.metaKey)
		{
			e.preventDefault();
		}
	}, { passive: false });

	// prevent middle-mouse scrolling
	document.addEventListener('DOMMouseScroll', function (e)
	{
		e.preventDefault();
	});
	document.body.style.overflow = false ? "" : "hidden";

	// disable browser context menu
	document.addEventListener('contextmenu', function (e)
	{
		e.preventDefault();
	});

	// Prevent the browser from scrolling with the middle mouse button
	document.addEventListener('mousedown', function (e)
	{
		if (e.button === 1)
		{
			e.preventDefault();
		}
	});

	// prevent keyboard input
	document.addEventListener('keydown', function (e)
	{
		const nativeInputFocused = NativeElementTags.includes(document.activeElement?.tagName || "");
		if (e.altKey)
		{
			// prevent back/forward page navigation
			if (e.keyCode === KeyCodes.RIGHT_ARROW || e.keyCode === KeyCodes.LEFT_ARROW)
			{
				e.preventDefault();
			}
		}
		if (e.ctrlKey || e.metaKey)
		{
			// undo/redo in input fields
			if (e.keyCode === KeyCodes.Z || e.keyCode === KeyCodes.Y)
			{
				// if no input field is selected specifically, prevent default browser behavior
				if (!nativeInputFocused)
				{
					e.preventDefault();
				}
			}
			// select all
			if (e.keyCode === KeyCodes.A)
			{
				if (!nativeInputFocused)
				{
					e.preventDefault();
				}
			}

			const forbiddenKeys = new Set([
				KeyCodes.S, // save page
				KeyCodes.F, // search page
				KeyCodes.G, // next search result
				KeyCodes.D, // bookmark / bookmark all
				KeyCodes.E, // start google search (chrome)
				KeyCodes.L, // focus search bar
				KeyCodes.I, // page info @ firefox
				KeyCodes.R, // refresh
				KeyCodes.N, // new browser window
				KeyCodes.W, // close tab / all tabs
				KeyCodes.T, // new tab / restore tab
				KeyCodes.B, // toggle bookmarks bar
				KeyCodes.O, // open / bookmark manager
				KeyCodes.J, // downloads page
				KeyCodes.H, // history page
				KeyCodes.U, // page source
				KeyCodes.P, // print
				KeyCodes.K, // search
				KeyCodes.TAB, // next tab / previous
				KeyCodes.PAGE_DOWN, // next tab
				KeyCodes.PAGE_UP, // previous tab
				KeyCodes.ADD, // zoom in
				KeyCodes.SUBTRACT, // zoom out
				KeyCodes.EQUALS, // zoom in
				KeyCodes.DASH, // zoom out
			]);
			if (forbiddenKeys.has(e.keyCode))
			{
				e.preventDefault();
			}
		}
	});

	// prevent arrow navigation (taken from: https://stackoverflow.com/a/8916697/618726)
	window.addEventListener("keydown", function (e)
	{
		if (WorkspaceUtils.shouldAllowNativeBehavior(true))
		{ return; }

		if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].indexOf(e.code) > -1)
		{
			e.preventDefault();
		}
	}, false);
}